// Country + dial-code data for the WhatsApp-OTP login picker.
//
// Full static list — this is reference data that never changes, so it lives in
// the bundle rather than behind an API or a dependency (no libphonenumber, no
// countries package for what a static table covers). Flag emoji is DERIVED from
// the ISO code (regional-indicator letters), so there's nothing to maintain per
// row beyond [code, name, dial].
//
// Per-country national-number length is NOT tracked for every country — the
// backend validates the number, and per-country length tables drift. We keep an
// exact length only for the markets that need a tight input (India especially,
// the live prod path); everything else uses a generic 6–15 gate (ITU E.164).
//
// Wire format: send the BARE national number plus a separate `dialCode` param
// (see the International Shipping backend guide) — the FE no longer prefixes the
// dial into the number.

export const DEFAULT_CODE = "IN";

// Diaspora markets an Indian handloom store actually ships to — pinned above the
// long alphabetical list so NRIs don't scroll.
export const POPULAR_CODES = ["US", "GB", "AE", "CA", "AU", "SG"];

// ISO alpha-2 → flag emoji via regional-indicator symbols.
const flagFor = (code) =>
  code
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65));

// Exact national-number length — only where we want a tight input/validation.
// India (10) is the live prod path and MUST stay exact. Others here are nicety;
// any code absent from this map falls back to the 6–15 range gate.
const LEN = {
  IN: 10, US: 10, CA: 10, GB: 10, AE: 9, AU: 9, SG: 8, BD: 10,
};

// Placeholder shown in the phone field. Only India kept (preserves the existing
// look); everything else uses the generic placeholder in the UI.
const SAMPLE = { IN: "98765 43210" };

// [ISO alpha-2, name, dial code (no +)]. Alphabetical by name after the pinned
// popular set is applied at render time.
const RAW = [
  ["IN", "India", "91"],
  ["US", "United States", "1"],
  ["GB", "United Kingdom", "44"],
  ["AE", "United Arab Emirates", "971"],
  ["CA", "Canada", "1"],
  ["AU", "Australia", "61"],
  ["SG", "Singapore", "65"],
  ["AF", "Afghanistan", "93"],
  ["AL", "Albania", "355"],
  ["DZ", "Algeria", "213"],
  ["AO", "Angola", "244"],
  ["AR", "Argentina", "54"],
  ["AM", "Armenia", "374"],
  ["AT", "Austria", "43"],
  ["AZ", "Azerbaijan", "994"],
  ["BH", "Bahrain", "973"],
  ["BD", "Bangladesh", "880"],
  ["BB", "Barbados", "1"],
  ["BY", "Belarus", "375"],
  ["BE", "Belgium", "32"],
  ["BZ", "Belize", "501"],
  ["BJ", "Benin", "229"],
  ["BT", "Bhutan", "975"],
  ["BO", "Bolivia", "591"],
  ["BA", "Bosnia and Herzegovina", "387"],
  ["BW", "Botswana", "267"],
  ["BR", "Brazil", "55"],
  ["BN", "Brunei", "673"],
  ["BG", "Bulgaria", "359"],
  ["BF", "Burkina Faso", "226"],
  ["KH", "Cambodia", "855"],
  ["CM", "Cameroon", "237"],
  ["CV", "Cape Verde", "238"],
  ["CL", "Chile", "56"],
  ["CN", "China", "86"],
  ["CO", "Colombia", "57"],
  ["CR", "Costa Rica", "506"],
  ["HR", "Croatia", "385"],
  ["CU", "Cuba", "53"],
  ["CY", "Cyprus", "357"],
  ["CZ", "Czech Republic", "420"],
  ["DK", "Denmark", "45"],
  ["DO", "Dominican Republic", "1"],
  ["EC", "Ecuador", "593"],
  ["EG", "Egypt", "20"],
  ["SV", "El Salvador", "503"],
  ["EE", "Estonia", "372"],
  ["ET", "Ethiopia", "251"],
  ["FJ", "Fiji", "679"],
  ["FI", "Finland", "358"],
  ["FR", "France", "33"],
  ["GE", "Georgia", "995"],
  ["DE", "Germany", "49"],
  ["GH", "Ghana", "233"],
  ["GR", "Greece", "30"],
  ["GT", "Guatemala", "502"],
  ["HN", "Honduras", "504"],
  ["HK", "Hong Kong", "852"],
  ["HU", "Hungary", "36"],
  ["IS", "Iceland", "354"],
  ["ID", "Indonesia", "62"],
  ["IR", "Iran", "98"],
  ["IQ", "Iraq", "964"],
  ["IE", "Ireland", "353"],
  ["IL", "Israel", "972"],
  ["IT", "Italy", "39"],
  ["JM", "Jamaica", "1"],
  ["JP", "Japan", "81"],
  ["JO", "Jordan", "962"],
  ["KZ", "Kazakhstan", "7"],
  ["KE", "Kenya", "254"],
  ["KW", "Kuwait", "965"],
  ["KG", "Kyrgyzstan", "996"],
  ["LA", "Laos", "856"],
  ["LV", "Latvia", "371"],
  ["LB", "Lebanon", "961"],
  ["LY", "Libya", "218"],
  ["LT", "Lithuania", "370"],
  ["LU", "Luxembourg", "352"],
  ["MO", "Macau", "853"],
  ["MK", "North Macedonia", "389"],
  ["MG", "Madagascar", "261"],
  ["MW", "Malawi", "265"],
  ["MY", "Malaysia", "60"],
  ["MV", "Maldives", "960"],
  ["ML", "Mali", "223"],
  ["MT", "Malta", "356"],
  ["MU", "Mauritius", "230"],
  ["MX", "Mexico", "52"],
  ["MD", "Moldova", "373"],
  ["MC", "Monaco", "377"],
  ["MN", "Mongolia", "976"],
  ["ME", "Montenegro", "382"],
  ["MA", "Morocco", "212"],
  ["MZ", "Mozambique", "258"],
  ["MM", "Myanmar", "95"],
  ["NA", "Namibia", "264"],
  ["NP", "Nepal", "977"],
  ["NL", "Netherlands", "31"],
  ["NZ", "New Zealand", "64"],
  ["NI", "Nicaragua", "505"],
  ["NG", "Nigeria", "234"],
  ["NO", "Norway", "47"],
  ["OM", "Oman", "968"],
  ["PK", "Pakistan", "92"],
  ["PA", "Panama", "507"],
  ["PG", "Papua New Guinea", "675"],
  ["PY", "Paraguay", "595"],
  ["PE", "Peru", "51"],
  ["PH", "Philippines", "63"],
  ["PL", "Poland", "48"],
  ["PT", "Portugal", "351"],
  ["QA", "Qatar", "974"],
  ["RO", "Romania", "40"],
  ["RU", "Russia", "7"],
  ["RW", "Rwanda", "250"],
  ["SA", "Saudi Arabia", "966"],
  ["SN", "Senegal", "221"],
  ["RS", "Serbia", "381"],
  ["SC", "Seychelles", "248"],
  ["SL", "Sierra Leone", "232"],
  ["SK", "Slovakia", "421"],
  ["SI", "Slovenia", "386"],
  ["SO", "Somalia", "252"],
  ["ZA", "South Africa", "27"],
  ["KR", "South Korea", "82"],
  ["ES", "Spain", "34"],
  ["LK", "Sri Lanka", "94"],
  ["SD", "Sudan", "249"],
  ["SE", "Sweden", "46"],
  ["CH", "Switzerland", "41"],
  ["SY", "Syria", "963"],
  ["TW", "Taiwan", "886"],
  ["TJ", "Tajikistan", "992"],
  ["TZ", "Tanzania", "255"],
  ["TH", "Thailand", "66"],
  ["TT", "Trinidad and Tobago", "1"],
  ["TN", "Tunisia", "216"],
  ["TR", "Turkey", "90"],
  ["TM", "Turkmenistan", "993"],
  ["UG", "Uganda", "256"],
  ["UA", "Ukraine", "380"],
  ["UY", "Uruguay", "598"],
  ["UZ", "Uzbekistan", "998"],
  ["VE", "Venezuela", "58"],
  ["VN", "Vietnam", "84"],
  ["YE", "Yemen", "967"],
  ["ZM", "Zambia", "260"],
  ["ZW", "Zimbabwe", "263"],
];

export const COUNTRIES = RAW.map(([code, name, dial]) => ({
  code,
  name,
  dial,
  flag: flagFor(code),
  len: LEN[code], // undefined for the long tail → range gate
  sample: SAMPLE[code],
}));

export const findCountry = (code) =>
  COUNTRIES.find((c) => c.code === code) || COUNTRIES[0];

// Popular list minus whatever is currently selected (it already shows under
// "Selected", so don't repeat it).
export const popularCountries = (selectedCode) =>
  POPULAR_CODES.filter((c) => c !== selectedCode).map(findCountry);

// Everything that isn't Selected or Popular, alphabetical — the "All countries"
// group.
export const otherCountries = (selectedCode) =>
  COUNTRIES.filter(
    (c) => c.code !== selectedCode && !POPULAR_CODES.includes(c.code),
  ).sort((a, b) => a.name.localeCompare(b.name));

// Type-to-filter: match on country name OR dial code.
export const filterCountries = (query) => {
  const q = (query || "").trim().toLowerCase();
  if (!q) return [];
  const qDigits = q.replace(/\D/g, "");
  return COUNTRIES.filter((c) => {
    const nameHit = c.name.toLowerCase().includes(q);
    const dialHit = qDigits !== "" && c.dial.includes(qDigits);
    return nameHit || dialHit;
  });
};

// "9876543210" + "91" → "+91 98765 ***10" (first 5 + last 2 shown, rest masked).
export const maskMobile = (dial, digits) => {
  const d = digits || "";
  if (d.length <= 4) return `+${dial} ${d}`;
  return `+${dial} ${d.slice(0, 5)} ***${d.slice(-2)}`;
};

// Input cap for the phone field. Exact length where known, else the E.164 max.
export const maxLen = (country) => country?.len || 15;

// Normalize a raw phone input down to the BARE national number for `country`.
// Paste / browser autofill can arrive formatted and with the country code
// (e.g. "+1 (202) 555-0100"): strip non-digits, drop a leading dial code ONLY
// when the input clearly carried one — an explicit "+" prefix, or digits that
// over-run the known national length by the dial length — then cap to maxLen.
// A genuine national number that merely starts with the dial digits is left
// intact (that's why the old startsWith(dial) heuristic was unsafe).
export const toNationalNumber = (raw = "", country) => {
  const hadPlus = String(raw).trimStart().startsWith("+");
  let digits = String(raw).replace(/\D/g, "");
  const dial = country?.dial;
  // An international call prefix in front of the dial code ("00" most places, "011" from the US) is
  // the same as a "+": "0061 4…" is +61 4….
  const exitPrefix = ["00", "011"].find((p) => dial && digits.startsWith(p + dial));
  if (exitPrefix) {
    digits = digits.slice(exitPrefix.length + dial.length);
  } else if (dial && digits.startsWith(dial)) {
    const overRuns = country?.len && digits.length > country.len;
    if (hadPlus || overRuns) digits = digits.slice(dial.length);
  }
  // A local trunk "0" (Australia "04…", UK "07…", India "09…") makes a real number one digit longer
  // than the country's length. Drop that 0 -- capping first cut off the LAST digit instead and stored
  // a wrong number.
  if (country?.len && digits.length > country.len && digits.startsWith("0")) {
    digits = digits.slice(1);
  }
  return digits.slice(0, maxLen(country));
};

// Is the typed national number complete enough to enable "Send"? Exact match
// where we track a length (India = 10), else a lenient ITU range (backend does
// the authoritative validation).
export const isCompleteNumber = (country, mobile) => {
  const n = (mobile || "").length;
  return country?.len ? n === country.len : n >= 6 && n <= 15;
};

// Razorpay Checkout's prefill.contact. Razorpay assumes +91 when the number has no country code, so a
// non-Indian number must carry "+<dial>". Indian numbers stay the bare last 10 digits, exactly as before.
export const razorpayContact = (digits = "", dial) => {
  const code = String(dial ?? "").replace(/\D/g, "");
  return code && code !== "91" ? `+${code}${digits}` : String(digits).slice(-10);
};
